import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Loader2, Mail, Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { shareCareGuideByEmail } from '@/features/chatbot/api/shareCareGuideApi';

type ShareCareGuideModalProps = {
  open: boolean;
  sessionId: string;
  planLabel: string;
  onClose: () => void;
};

type ShareCareGuideVariables = {
  recipientEmail: string;
  recipientName: string;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Care-seeker care-guide share modal. Email-only: the care seeker sends the
 * care-guide PDF to any address (a family member, themselves, …). The FP share
 * flow (client picker, in-app delivery) doesn't apply here, so this is a single
 * email step rather than reusing the FP ShareCarePlanModal.
 */
export const ShareCareGuideModal = ({
  open,
  sessionId,
  planLabel,
  onClose,
}: ShareCareGuideModalProps) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const mutation = useMutation({
    mutationFn: ({ recipientEmail, recipientName }: ShareCareGuideVariables) =>
      shareCareGuideByEmail(sessionId, recipientEmail, recipientName),
  });

  // Reset transient state whenever the dialog is (re)opened or closed so a
  // previous send/success/error never leaks into the next open.
  useEffect(() => {
    if (!open) {
      setEmail('');
      setName('');
      mutation.reset();
    }
    // mutation.reset is stable; key off `open` only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const emailValid = EMAIL_RE.test(email.trim());
  const submittedEmail = mutation.data?.recipientEmail ?? mutation.variables?.recipientEmail;

  const handleSend = () => {
    if (!emailValid || mutation.isPending) return;
    mutation.mutate({
      recipientEmail: email.trim(),
      recipientName: name.trim(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-text-primary">
            Share &ldquo;{planLabel}&rdquo;
          </DialogTitle>
          <DialogDescription className="text-sm text-text-secondary">
            Send this care guide as a PDF to an email address — a family member, or yourself.
          </DialogDescription>
        </DialogHeader>

        {mutation.isSuccess ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <CheckCircle2 className="h-10 w-10 text-sage" aria-hidden />
            <p className="text-sm text-text-primary">
              Your care guide is on its way to{' '}
              <span className="font-medium">{submittedEmail}</span>.
            </p>
            <Button type="button" className="mt-2" onClick={onClose}>
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="share-care-guide-email">Recipient email</Label>
              <div className="relative">
                <Mail
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary"
                  aria-hidden
                />
                <Input
                  id="share-care-guide-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={mutation.isPending}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="share-care-guide-name">Recipient name (optional)</Label>
              <Input
                id="share-care-guide-name"
                type="text"
                autoComplete="name"
                placeholder="Pat Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={mutation.isPending}
              />
            </div>

            {mutation.isError && (
              <p className="text-sm text-red-600" role="alert">
                We couldn&apos;t send the care guide right now. Please try again.
              </p>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSend}
                disabled={!emailValid || mutation.isPending}
                className="gap-2 bg-sage text-white hover:bg-[hsl(169_95%_18%)]"
              >
                {mutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Send className="h-4 w-4" aria-hidden />
                )}
                {mutation.isPending ? 'Sending…' : 'Send care guide'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
